/*
 * Description:
 * ---------------------
 * Pilot Insights — the headline view for pilot deployments.
 *
 * Reads the telemetry framework exposed by notabox-server at
 *   /api/v1/telemetry/{scorecard,timeseries,events,apps,devices}
 * (see docs/telemetry-framework.md) and renders the per-node KPI sheet:
 *
 *   • Headline scorecard (uptime, gain multiplier, MTTR, sessions preserved)
 *   • KPI grid with at-a-glance numbers
 *   • Per-WAN ranking table
 *   • Per-WAN latency sparklines (1h / 6h / 24h / 7d window)
 *   • Top apps + top devices by bytes
 *   • Recent events feed
 *   • What the appliance costs to run (from the edge's own power sampler)
 *
 * The data path is fully decoupled from the edge — these panels render
 * even if the edge is offline, because every figure is computed by
 * notabox-server from previously-ingested telemetry.
 *
 * ! file PilotInsightsPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2026
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { useWanName } from "./WanNames";
import { fmtBytes, fmtNum } from "../utils/formatters";
import useVisibleInterval from "../hooks/useVisibleInterval";

// ── Formatting helpers ──────────────────────────────────────────────────────

const fmtPct = (v, prec = 2) =>
  v == null || Number.isNaN(v) ? "—" : `${(v * 100).toFixed(prec)}%`;

const fmtMs = (v) => (v == null ? "—" : `${v.toFixed(1)} ms`);

const fmtSec = (v) => {
  if (v == null) return "—";
  if (v < 60) return `${v.toFixed(1)}s`;
  if (v < 3600) return `${(v / 60).toFixed(1)}m`;
  return `${(v / 3600).toFixed(1)}h`;
};

const fmtMbps = (v) => (v == null || v <= 0 ? "—" : `${v.toFixed(1)} Mbps`);

const fmtGain = (v) => (v == null || !isFinite(v) || v <= 0 ? "—" : `${v.toFixed(2)}×`);

const fmtTs = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const severityColor = (s) => {
  switch (s) {
    case "error":
    case "critical":
      return { bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5" };
    case "warn":
    case "warning":
      return { bg: "#fef3c7", color: "#a16207", border: "#fcd34d" };
    case "info":
    default:
      return { bg: "#e0f2fe", color: "#0369a1", border: "#7dd3fc" };
  }
};

const eventGlyph = (type) => {
  if (!type) return "•";
  if (type.startsWith("wan_")) return type === "wan_up" ? "▲" : "▼";
  if (type === "failover") return "⤳";
  if (type === "cloud_switch") return "☁";
  if (type === "boot") return "⚡";
  if (type === "shutdown") return "⏻";
  if (type.startsWith("qos_")) return "★";
  if (type === "crash") return "✖";
  return "•";
};

// ── Window choices ──────────────────────────────────────────────────────────

const WINDOWS = [
  { key: "1h", label: "Last 1 h", seconds: 3600 },
  { key: "6h", label: "Last 6 h", seconds: 6 * 3600 },
  { key: "24h", label: "Last 24 h", seconds: 24 * 3600 },
  { key: "7d", label: "Last 7 d", seconds: 7 * 24 * 3600 },
];
// Per-series query limit. The edge ships at 10 s cadence by default, so a
// 7-day window has ~60 480 samples per metric. We ask for the full set; the
// chart layer downsamples for rendering. Older builds passed limit=2000,
// which silently truncated anything past ~5.5 h.
const QUERY_LIMIT = 80000;

// The shared default (5 s) is sized for edge polling, where a slow box must
// fail fast. These are server-side telemetry queries returning tens of
// thousands of samples, so 5 s times out on a 7-day window and the whole
// refresh is discarded. Give them their own budget.
const TELEMETRY_TIMEOUT_MS = 20000;

// The event feed is a small in-memory scan on the server, so it can be polled
// far faster than the sample queries. It is also what makes a hard failover
// visible: a new wan_down/failover event triggers an immediate full refresh
// instead of waiting out the remainder of the 15 s cycle. The floor on that
// latency is the edge's own telemetry flush interval (15 s), not this poll.
const EVENT_POLL_MS = 5000;
const FULL_POLL_MS = 15000;

// Event types that change what every other panel on this page is showing.
// qos_breach is deliberately absent: it fires on every threshold blip and
// would turn the cheap poll into a constant full refresh.
const DISRUPTIVE_EVENTS = new Set([
  "wan_down",
  "wan_up",
  "failover",
  "cloud_switch",
  "qos_switch",
  "crash",
]);

// Identity for an event: the server assigns no id, and (ts, type, wan) is
// unique in practice because one WAN cannot transition twice in one instant.
const eventKey = (e) =>
  e ? `${e.ts}|${e.type}|${e.tags?.wan || ""}` : "";

const tfetch = (url) => fetchWithTimeout(url, {}, TELEMETRY_TIMEOUT_MS);

// Maximum points the SVG chart will draw per series. Above this we run a
// stride-and-bucket downsampler that preserves min/max within each bucket
// (so spikes don't disappear) while keeping the path manageable. ~600 maps
// to roughly 1 px per point on a 700 px wide chart — visually lossless.
const CHART_MAX_POINTS = 600;

function downsamplePoints(points, target = CHART_MAX_POINTS) {
  if (!points || points.length <= target) return points || [];
  // Min/max bucket downsampler: split the series into `target/2` buckets
  // and emit two points per bucket (the min and max value). This preserves
  // peaks (latency spikes, loss bursts) that a naive stride sampler would
  // wash out, while cutting the path to <= target vertices.
  const buckets = Math.max(1, Math.floor(target / 2));
  const step = points.length / buckets;
  const out = [];
  for (let i = 0; i < buckets; i++) {
    const lo = Math.floor(i * step);
    const hi = Math.min(points.length, Math.floor((i + 1) * step));
    if (hi <= lo) continue;
    let minP = points[lo];
    let maxP = points[lo];
    for (let k = lo + 1; k < hi; k++) {
      if (points[k].value < minP.value) minP = points[k];
      if (points[k].value > maxP.value) maxP = points[k];
    }
    // Emit in chronological order so the SVG path doesn't zigzag.
    if (minP.ts < maxP.ts) {
      out.push(minP, maxP);
    } else if (minP.ts > maxP.ts) {
      out.push(maxP, minP);
    } else {
      out.push(minP);
    }
  }
  return out;
}
// ── Reusable card primitives ────────────────────────────────────────────────

function Card({ title, subtitle, children, accent }) {
  return (
    <div
      style={{
        background: "white",
        borderRadius: 12,
        border: "1px solid #e2e8f0",
        padding: "1.1rem 1.25rem",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        borderTop: accent ? `3px solid ${accent}` : undefined,
        display: "flex",
        flexDirection: "column",
        gap: "0.65rem",
      }}
    >
      {(title || subtitle) && (
        <div>
          {title && (
            <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#475569", letterSpacing: "0.02em", textTransform: "uppercase" }}>
              {title}
            </div>
          )}
          {subtitle && (
            <div style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

function KPI({ label, value, sub, accent }) {
  return (
    <Card title={label} accent={accent}>
      <div style={{ fontSize: "1.7rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: "0.78rem", color: "#64748b" }}>{sub}</div>}
    </Card>
  );
}

// ── Failover timeline ───────────────────────────────────────────────────────
// Structured visualization of failover events. Each row is a clickable card
// that expands to show the full set of tags that explain *why* the WAN was
// taken out of rotation. Failover events with cause="link_down" are paired
// with the next subsequent wan_up on the same WAN to show recovery time.

const fmtAbsTs = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "—";
  return (
    d.toLocaleDateString([], { month: "short", day: "numeric" }) +
    " " +
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
  );
};

const fmtAgo = (ts) => {
  if (!ts) return "—";
  const t = new Date(ts).getTime();
  if (isNaN(t)) return "—";
  const sec = Math.max(0, (Date.now() - t) / 1000);
  if (sec < 60) return `${Math.round(sec)}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
};

// Maps a failover event's `cause` tag → visual treatment (icon, label, palette).
const causeMeta = (cause) => {
  switch (cause) {
    case "link_down":
      return {
        glyph: "⛔",
        label: "Link down",
        color: "#b91c1c",
        bg: "#fef2f2",
        border: "#fca5a5",
        explain: "Underlying interface lost link or was removed; WAN socket torn down.",
      };
    case "degraded":
      return {
        glyph: "⚠",
        label: "Degraded",
        color: "#a16207",
        bg: "#fef3c7",
        border: "#fcd34d",
        explain: "Active health probes flagged the WAN (loss / latency / probe miss).",
      };
    default:
      return {
        glyph: "⤳",
        label: cause || "Failover",
        color: "#475569",
        bg: "#f1f5f9",
        border: "#cbd5e1",
        explain: "WAN removed from active rotation.",
      };
  }
};

// Pretty-printer for the diagnostic `reason` tag emitted by the health
// aggregator. Translates cryptic identifiers to readable phrases so an
// operator can understand the failover at a glance without expanding it.
const prettyReason = (reason) => {
  if (!reason) return "";
  const map = {
    loss_threshold: "packet loss above threshold",
    rtt_threshold: "latency above threshold",
    probe_miss: "consecutive probe acks missed",
    no_route: "no usable route",
    interface_down: "interface administratively down",
  };
  return map[reason] || reason;
};

function FailoverChip({ active, onClick, color = "#0f172a", children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? color : "white",
        color: active ? "white" : "#475569",
        border: `1px solid ${active ? color : "#cbd5e1"}`,
        borderRadius: 999,
        padding: "0.25rem 0.7rem",
        fontSize: "0.75rem",
        fontWeight: 600,
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "background 80ms",
      }}
    >
      {children}
    </button>
  );
}

function FailoverRow({ f, expanded, onToggle, isLast }) {
  const wanName = useWanName();
  const meta = causeMeta(f._cause);
  const wanColor = colorForWan(f._wan || "");
  const recovered = !!f._recoveredAt;
  const recoveredSec = recovered
    ? Math.max(0, (new Date(f._recoveredAt).getTime() - new Date(f.ts).getTime()) / 1000)
    : null;
  const fmtRecovered = (s) =>
    s == null ? "" : s < 60 ? `${s.toFixed(0)}s` : s < 3600 ? `${(s / 60).toFixed(1)}m` : `${(s / 3600).toFixed(1)}h`;

  // Two-column row: time gutter + marker rail + content card.
  // The marker rail is a 24px-wide column with the vertical timeline line and
  // a colored dot at the row's vertical center. Each row paints its own
  // segment of the line, so the line spans the full list naturally.
  return (
    <div style={{ display: "flex", alignItems: "stretch", gap: 0, position: "relative" }}>
      {/* Time gutter */}
      <div
        style={{
          width: 96,
          flexShrink: 0,
          paddingTop: 14,
          paddingRight: 10,
          textAlign: "right",
          color: "#475569",
        }}
      >
        <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>
          {fmtAgo(f.ts)}
        </div>
        <div style={{ fontSize: "0.68rem", color: "#94a3b8", marginTop: 2 }}>
          {(() => {
            const d = new Date(f.ts);
            return isNaN(d.getTime())
              ? ""
              : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
          })()}
        </div>
      </div>

      {/* Marker rail */}
      <div
        style={{
          width: 24,
          flexShrink: 0,
          position: "relative",
          alignSelf: "stretch",
        }}
      >
        {/* vertical line — drawn for every row except we let the dot sit on top */}
        <div
          style={{
            position: "absolute",
            left: 11,
            top: 0,
            bottom: isLast ? "calc(100% - 18px)" : 0,
            width: 2,
            background: "#e2e8f0",
          }}
        />
        {/* dot */}
        <div
          style={{
            position: "absolute",
            left: 4,
            top: 12,
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: meta.color,
            border: "3px solid white",
            boxShadow: `0 0 0 2px ${meta.color}`,
            zIndex: 1,
          }}
          title={meta.label}
        />
      </div>

      {/* Card */}
      <div
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        style={{
          flex: 1,
          minWidth: 0,
          marginLeft: 8,
          marginBottom: 12,
          border: `1px solid ${meta.border}`,
          background: "white",
          borderRadius: 10,
          cursor: "pointer",
          overflow: "hidden",
          boxShadow: expanded ? "0 4px 12px rgba(15,23,42,0.08)" : "0 1px 2px rgba(15,23,42,0.04)",
          transition: "box-shadow 120ms",
        }}
        title={fmtAbsTs(f.ts)}
      >
        {/* Left color stripe + header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.6rem",
            padding: "0.55rem 0.75rem 0.55rem 0.65rem",
            borderLeft: `4px solid ${meta.color}`,
            fontSize: "0.85rem",
            flexWrap: "wrap",
          }}
        >
          {/* WAN chip */}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 999,
              padding: "0.15rem 0.55rem",
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: wanColor,
              }}
            />
            {f._wan ? wanName(f._wan) : "(unknown WAN)"}
          </span>

          {/* cause label */}
          <span
            style={{
              color: meta.color,
              fontWeight: 700,
              background: meta.bg,
              border: `1px solid ${meta.border}`,
              borderRadius: 6,
              padding: "0.1rem 0.45rem",
              fontSize: "0.78rem",
            }}
          >
            {meta.label}
          </span>

          {/* reason */}
          {f._reason && (
            <span style={{ color: "#475569", flex: "1 1 200px", minWidth: 0 }}>
              {prettyReason(f._reason)}
            </span>
          )}
          {!f._reason && <span style={{ flex: "1 1 0", minWidth: 0 }} />}

          {/* recovery badge */}
          {recovered ? (
            <span
              style={{
                background: "#dcfce7",
                color: "#166534",
                border: "1px solid #86efac",
                borderRadius: 6,
                padding: "0.1rem 0.45rem",
                fontSize: "0.72rem",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              ↺ recovered in {fmtRecovered(recoveredSec)}
            </span>
          ) : f._cause === "link_down" ? (
            <span
              style={{
                background: "#fef2f2",
                color: "#991b1b",
                border: "1px solid #fca5a5",
                borderRadius: 6,
                padding: "0.1rem 0.45rem",
                fontSize: "0.72rem",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              not recovered in window
            </span>
          ) : null}

          {/* chevron */}
          <span
            style={{
              color: "#94a3b8",
              fontSize: "0.85rem",
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 120ms",
              display: "inline-block",
              width: 14,
              textAlign: "center",
              flexShrink: 0,
            }}
          >
            ▶
          </span>
        </div>

        {/* expanded body */}
        {expanded && (
          <div
            style={{
              borderTop: `1px solid ${meta.border}`,
              background: "#f8fafc",
              padding: "0.75rem 1rem",
              display: "flex",
              flexDirection: "column",
              gap: "0.6rem",
              fontSize: "0.82rem",
            }}
          >
            <div style={{ color: "#475569" }}>{meta.explain}</div>

            {f.message && (
              <div style={{ color: "#0f172a" }}>
                <span style={{ color: "#94a3b8", marginRight: 6 }}>message:</span>
                {f.message}
              </div>
            )}

            {/* tag table */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "0.4rem 1rem",
              }}
            >
              <DetailRow label="Time" value={fmtAbsTs(f.ts)} />
              <DetailRow label="Source" value={f.source || "—"} />
              {f._wan && <DetailRow label="WAN" value={wanName(f._wan)} />}
              <DetailRow label="Cause" value={meta.label} />
              {f._reason && <DetailRow label="Reason" value={prettyReason(f._reason)} />}
              {recovered && <DetailRow label="Recovered at" value={fmtAbsTs(f._recoveredAt)} />}
              {recovered && (
                <DetailRow label="Downtime" value={fmtRecovered(recoveredSec)} />
              )}
            </div>

            {/* extra raw tags not already shown */}
            {(() => {
              const shown = new Set(["wan", "cause", "reason"]);
              const extras = Object.entries(f.tags || {}).filter(([k]) => !shown.has(k));
              if (extras.length === 0) return null;
              return (
                <div>
                  <div style={{ color: "#94a3b8", fontSize: "0.7rem", marginBottom: 4 }}>
                    Additional tags
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
                    {extras.map(([k, v]) => (
                      <span
                        key={k}
                        style={{
                          background: "white",
                          border: "1px solid #e2e8f0",
                          borderRadius: 6,
                          padding: "0.15rem 0.4rem",
                          fontSize: "0.72rem",
                          color: "#475569",
                          fontFamily: "ui-monospace, SFMono-Regular, monospace",
                        }}
                      >
                        {k}={String(v)}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <span style={{ color: "#94a3b8", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </span>
      <span style={{ color: "#0f172a", fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function FailoverTimeline({ events }) {
  const wanName = useWanName();
  // Pair each `failover` event with the next `wan_up` event on the same WAN
  // to compute recovery latency. Pairing is local to the loaded events
  // window; if the recovery happened outside the window it stays unpaired.
  const enriched = useMemo(() => {
    const failovers = events.filter((e) => e.type === "failover");
    const wanUps = events.filter((e) => e.type === "wan_up");
    return failovers.map((f) => {
      const wan = f.tags?.wan;
      const cause = f.tags?.cause || "";
      const reason = f.tags?.reason || "";
      const fts = new Date(f.ts).getTime();
      let recoveredAt = null;
      if (wan && cause === "link_down") {
        const cands = wanUps
          .filter((u) => u.tags?.wan === wan && new Date(u.ts).getTime() > fts)
          .sort((a, b) => new Date(a.ts) - new Date(b.ts));
        if (cands.length > 0) recoveredAt = cands[0].ts;
      }
      return { ...f, _wan: wan, _cause: cause, _reason: reason, _recoveredAt: recoveredAt };
    });
  }, [events]);

  const wanList = useMemo(
    () => Array.from(new Set(enriched.map((f) => f._wan).filter(Boolean))).sort(),
    [enriched]
  );

  const counts = useMemo(() => {
    const c = { all: enriched.length, link_down: 0, degraded: 0, other: 0 };
    enriched.forEach((f) => {
      if (f._cause === "link_down") c.link_down++;
      else if (f._cause === "degraded") c.degraded++;
      else c.other++;
    });
    return c;
  }, [enriched]);

  const [causeFilter, setCauseFilter] = useState("all");
  const [wanFilter, setWanFilter] = useState("all");
  const [expandedKey, setExpandedKey] = useState(null);

  const visible = useMemo(() => {
    return enriched.filter((f) => {
      if (causeFilter !== "all" && f._cause !== causeFilter) return false;
      if (wanFilter !== "all" && f._wan !== wanFilter) return false;
      return true;
    });
  }, [enriched, causeFilter, wanFilter]);

  if (enriched.length === 0) {
    return (
      <div style={{ color: "#94a3b8", fontSize: "0.85rem", padding: "0.25rem 0" }}>
        No failovers in this window. Bonded WAN is steady.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.7rem" }}>
      {/* filter bar */}
      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.7rem", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Cause
          </span>
          <FailoverChip active={causeFilter === "all"} onClick={() => setCauseFilter("all")} color="#475569">
            All ({counts.all})
          </FailoverChip>
          {counts.link_down > 0 && (
            <FailoverChip
              active={causeFilter === "link_down"}
              onClick={() => setCauseFilter("link_down")}
              color="#b91c1c"
            >
              Link down ({counts.link_down})
            </FailoverChip>
          )}
          {counts.degraded > 0 && (
            <FailoverChip
              active={causeFilter === "degraded"}
              onClick={() => setCauseFilter("degraded")}
              color="#a16207"
            >
              Degraded ({counts.degraded})
            </FailoverChip>
          )}
          {counts.other > 0 && (
            <FailoverChip
              active={causeFilter === "other"}
              onClick={() => setCauseFilter("other")}
              color="#475569"
            >
              Other ({counts.other})
            </FailoverChip>
          )}
        </div>

        {wanList.length > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.7rem", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              WAN
            </span>
            <FailoverChip active={wanFilter === "all"} onClick={() => setWanFilter("all")} color="#475569">
              All
            </FailoverChip>
            {wanList.map((w) => (
              <FailoverChip
                key={w}
                active={wanFilter === w}
                onClick={() => setWanFilter(w)}
                color={colorForWan(w)}
              >
                {wanName(w)}
              </FailoverChip>
            ))}
          </div>
        )}
      </div>

      {/* list */}
      {visible.length === 0 ? (
        <div style={{ color: "#94a3b8", fontSize: "0.85rem" }}>No failovers match the active filters.</div>
      ) : (
        <div
          style={{
            maxHeight: 520,
            overflowY: "auto",
            paddingTop: 4,
            paddingBottom: 4,
          }}
        >
          {visible.map((f, i) => {
            const key = `${f.ts}-${f._wan}-${f._cause}-${i}`;
            return (
              <FailoverRow
                key={key}
                f={f}
                expanded={expandedKey === key}
                onToggle={() => setExpandedKey(expandedKey === key ? null : key)}
                isLast={i === visible.length - 1}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Simple multi-series SVG line chart ──────────────────────────────────────

function Sparklines({ series, height = 160, yLabel = "ms" }) {
  // series: [{ name, color, points: [{ts(ms), value}] }]
  const all = series.flatMap((s) => s.points).filter((p) => p.value != null && isFinite(p.value));
  if (all.length === 0) {
    return (
      <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "0.85rem" }}>
        No data in this window.
      </div>
    );
  }
  const xs = all.map((p) => p.ts);
  const ys = all.map((p) => p.value);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = 0;
  const yMax = Math.max(...ys) * 1.15 || 1;
  const w = 700;
  const h = height;
  const pad = { l: 40, r: 12, t: 10, b: 22 };

  const scaleX = (x) =>
    pad.l + ((x - xMin) / Math.max(1, xMax - xMin)) * (w - pad.l - pad.r);
  const scaleY = (y) =>
    h - pad.b - ((y - yMin) / Math.max(0.0001, yMax - yMin)) * (h - pad.t - pad.b);

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height, display: "block" }}>
        {/* grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((g, i) => {
          const y = pad.t + g * (h - pad.t - pad.b);
          return (
            <g key={i}>
              <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke="#f1f5f9" />
              <text x={4} y={y + 3} fontSize="9" fill="#94a3b8">
                {Math.round(yMax * (1 - g))}
              </text>
            </g>
          );
        })}
        <text x={4} y={h - 6} fontSize="9" fill="#94a3b8">
          {yLabel}
        </text>
        {/* x axis labels — show start, midpoint, end so the operator can read
            granularity and locate events. Format adapts to span: short windows
            show HH:MM:SS, longer windows show date + HH:MM. */}
        {(() => {
          const span = xMax - xMin;
          const fmt = (t) => {
            const d = new Date(t);
            if (span <= 3 * 3600 * 1000) {
              return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
            }
            if (span <= 36 * 3600 * 1000) {
              return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
            }
            return d.toLocaleDateString([], { month: "short", day: "numeric" }) + " " +
              d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
          };
          const ticks = [
            { x: pad.l, t: xMin, anchor: "start" },
            { x: pad.l + (w - pad.l - pad.r) / 2, t: xMin + span / 2, anchor: "middle" },
            { x: w - pad.r, t: xMax, anchor: "end" },
          ];
          return ticks.map((tk, i) => (
            <text key={i} x={tk.x} y={h - 6} fontSize="9" fill="#94a3b8" textAnchor={tk.anchor}>
              {fmt(tk.t)}
            </text>
          ));
        })()}
        {/* lines */}
        {series.map((s, i) => {
          const pts = s.points.filter((p) => p.value != null && isFinite(p.value));
          if (pts.length < 2) return null;
          const d = pts
            .map((p, j) => `${j === 0 ? "M" : "L"}${scaleX(p.ts).toFixed(1)} ${scaleY(p.value).toFixed(1)}`)
            .join(" ");
          return (
            <path key={i} d={d} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          );
        })}
      </svg>
    </div>
  );
}

// ── Horizontal stacked-share bar list ───────────────────────────────────────

function ShareList({ items, color = "#6366f1", labelKey = "label", valueKey = "bytes", shareKey = "share", subKey, max = 8 }) {
  if (!items || items.length === 0) {
    return <div style={{ color: "#94a3b8", fontSize: "0.85rem", padding: "0.5rem 0" }}>No data yet.</div>;
  }
  const top = items.slice(0, max);
  const maxBytes = Math.max(...top.map((i) => i[valueKey] || 0), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      {top.map((it, idx) => {
        const pct = (it[valueKey] / maxBytes) * 100;
        return (
          <div key={idx}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", marginBottom: 4 }}>
              <span style={{ fontWeight: 600, color: "#0f172a" }}>
                {it[labelKey]}
                {subKey && it[subKey] ? (
                  <span style={{ color: "#94a3b8", fontWeight: 400, marginLeft: 6 }}>
                    {it[subKey]}
                  </span>
                ) : null}
              </span>
              <span style={{ color: "#475569" }}>
                {fmtBytes(it[valueKey])}
                {it[shareKey] != null && (
                  <span style={{ color: "#94a3b8", marginLeft: 6 }}>
                    {fmtPct(it[shareKey], 1)}
                  </span>
                )}
              </span>
            </div>
            <div style={{ height: 6, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
              <div
                style={{
                  height: "100%",
                  width: `${pct}%`,
                  background: color,
                  borderRadius: 4,
                  transition: "width 0.4s ease",
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Color palette for per-WAN series ────────────────────────────────────────
//
// IMPORTANT: colors are bound to the WAN *name* via a deterministic hash, NOT
// to the array index in which the WAN happens to appear in a given API
// response. The previous implementation used `WAN_COLORS[i]` over the
// insertion order of a Map<name, ...>, which meant wlan0 was indigo in one
// poll and green in the next depending on whether enp2s0 happened to appear
// first in that sample window. Keying off the name guarantees:
//   • wlan0 is always the same color in every chart, every poll, every panel
//   • adding or removing a WAN does not reshuffle the others
//   • the same color shows up in the WAN ranking table, the per-metric
//     sparkline legends, AND the reachability heat-strip
//
// djb2-ish hash is stable across browsers and JS engines.

const WAN_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#0ea5e9", "#ec4899", "#14b8a6"];

function colorForWan(name) {
  if (!name) return WAN_COLORS[0];
  let h = 5381;
  for (let i = 0; i < name.length; i++) {
    h = ((h << 5) + h + name.charCodeAt(i)) | 0; // h * 33 + c
  }
  return WAN_COLORS[Math.abs(h) % WAN_COLORS.length];
}

// ── Legend row shared by all sparkline cards ────────────────────────────────

function LegendRow({ series }) {
  const wanName = useWanName();
  if (!series || series.length === 0) return null;
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        fontSize: "0.78rem",
        color: "#475569",
        marginTop: 4,
      }}
    >
      {series.map((s) => (
        <span key={s.name} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              background: s.color,
              borderRadius: 2,
              display: "inline-block",
            }}
          />
          {wanName(s.name)}
        </span>
      ))}
    </div>
  );
}

// ── Probe-path reachability heat-strip ──────────────────────────────────────
//
// Visualizes, per WAN, when active control-plane probes were getting acked
// over the selected window. Each strip is bucketized into ~120 equal-width
// time slots; a slot is:
//   • green  if at least one sample in the slot has loss_ratio < 1.0
//            (= probes acking → control plane reachable through that WAN)
//   • red    if every sample in the slot has loss_ratio >= 1.0
//            (= full loss → WAN unreachable for probes)
//   • gray   if the slot has no samples (gap in telemetry, edge offline,
//            or WAN didn't exist yet)
//
// Data source is the same per-WAN loss_ratio series we already extract for
// the line charts, so legacy edges that don't emit loss_ratio simply
// render as empty strips ("no samples in this window") — no breakage.
const REACH_BUCKETS = 120;

// Number of horizontal time-slots used by the WAN state ribbon and the
// tunnel-wide outage strip. 200 gives a good visual density at typical
// dashboard widths without over-aggregating short outages.
const RIBBON_BUCKETS = 200;

// LossRatio threshold above which we color a WAN slot "degraded" on the state
// ribbon (kept independent of the edge's `loss_threshold` config so the visual
// stays meaningful even if the edge is using non-default thresholds). 5% over
// any individual probe round is well past "noise floor" for a real link.
const RIBBON_DEGRADED_THRESHOLD_PCT = 5;

// ── WAN down-interval overlay (from wan_down / wan_up events) ────────────────
//
// When a WAN link drops (e.g. cable unplugged), the edge stops emitting
// wan_link samples for it entirely — so its loss_ratio series has a *gap*,
// not a run of 100%-loss points. A naive heat-strip paints that gap "no data"
// (gray) when it should read "down" (red): the WAN was genuinely unreachable,
// we just have no probe samples because the interface is gone.
//
// The authoritative signal is the wan_down → wan_up event pair the edge
// records. buildWanDownIntervals pairs them per WAN into [start, end) ranges
// (end = Infinity while still down) so the strips can paint those slots red
// regardless of sample gaps.
//
// Not every wan_down is an outage. A phone tags a network it no longer has
// (it left the Wi-Fi, or the radio was turned off) cause "gone": that link is
// absent, not down, so its gap stays "no data" instead of reading as a fault.
function buildWanDownIntervals(events) {
  const byWan = new Map();
  const evs = (events || [])
    .filter((e) => e.type === "wan_up" || (e.type === "wan_down" && e.tags?.cause !== "gone"))
    .map((e) => ({
      wan: e.tags?.wan,
      down: e.type === "wan_down",
      t: new Date(e.ts).getTime(),
    }))
    .filter((e) => e.wan && isFinite(e.t))
    .sort((a, b) => a.t - b.t);
  const openDown = new Map();
  for (const e of evs) {
    if (e.down) {
      if (!openDown.has(e.wan)) openDown.set(e.wan, e.t);
    } else if (openDown.has(e.wan)) {
      const start = openDown.get(e.wan);
      openDown.delete(e.wan);
      if (!byWan.has(e.wan)) byWan.set(e.wan, []);
      byWan.get(e.wan).push({ start, end: e.t });
    }
  }
  for (const [wan, start] of openDown) {
    if (!byWan.has(wan)) byWan.set(wan, []);
    byWan.get(wan).push({ start, end: Infinity });
  }
  return byWan;
}

// True iff the time slot [t0, t1) overlaps any down interval for the WAN.
function slotIsDown(intervals, t0, t1) {
  if (!intervals) return false;
  for (const iv of intervals) {
    if (iv.start < t1 && iv.end > t0) return true;
  }
  return false;
}

// ── Stacked area chart (per-WAN throughput, per-app throughput, …) ─────────
//
// Series-stacked area, forward-filled bucket-aligned. The visual "story"
// here is the failover journey: when one WAN's band collapses to zero and a
// new band rises, the dashboard shows the bonded tunnel actually moving
// traffic across links. Total height = sum of all series at that instant
// → reads as combined tunnel throughput.
//
// Forward-fill matters: samples arrive at 10 s cadence, so without it every
// slot between samples would dip to 0 and the chart would look like saw-
// teeth. Forward-fill assumes "the last reading still applies until a new
// reading arrives" — the same assumption every step-fn telemetry chart in
// the wild makes. When a sample is missing (link disappears), we DO want
// it to read 0, but that case shows up as samples explicitly arriving with
// value=0 (the passive counter for a dead link reports 0 bps), not as a
// missing slot.
function StackedAreaChart({ series, height = 180, yLabel = "Mbps", emptyMsg }) {
  const allPts = (series || []).flatMap((s) => s.points || []);
  if (allPts.length === 0) {
    return (
      <div style={{ color: "#94a3b8", fontSize: "0.85rem", padding: "0.5rem 0" }}>
        {emptyMsg || "No data in this window."}
      </div>
    );
  }
  const tMin = Math.min(...allPts.map((p) => p.ts));
  const tMax = Math.max(...allPts.map((p) => p.ts));
  if (tMax <= tMin) {
    return <div style={{ color: "#94a3b8", fontSize: "0.85rem" }}>Insufficient data.</div>;
  }

  const N = 200;
  const slotMs = (tMax - tMin) / (N - 1);
  const valsByIdx = (series || []).map((s) => {
    const slots = new Array(N).fill(0);
    const sorted = (s.points || []).slice().sort((a, b) => a.ts - b.ts);
    // A rate holds until the next sample, and for no more than a few of this
    // series' own sample spacings. Past that the link sent nothing because it
    // was not there, a phone off overnight or an edge gone dark, and holding
    // its last rate drew traffic that never happened.
    const spacings = sorted.slice(1).map((pt, k) => pt.ts - sorted[k].ts).sort((x, y) => x - y);
    const typical = spacings.length ? spacings[Math.floor(spacings.length / 2)] : slotMs;
    const holdMs = Math.max(slotMs, 3 * typical);
    let p = 0;
    let last = 0;
    let lastTs = -Infinity;
    for (let i = 0; i < N; i++) {
      const slotEnd = tMin + (i + 0.5) * slotMs;
      while (p < sorted.length && sorted[p].ts <= slotEnd) {
        last = sorted[p].value;
        lastTs = sorted[p].ts;
        p++;
      }
      slots[i] = slotEnd - lastTs <= holdMs ? Math.max(0, last) : 0;
    }
    return slots;
  });
  const stacks = [];
  for (let s = 0; s < (series || []).length; s++) {
    const cum = new Array(N);
    for (let i = 0; i < N; i++) {
      cum[i] = (s > 0 ? stacks[s - 1][i] : 0) + valsByIdx[s][i];
    }
    stacks.push(cum);
  }
  const yMax = Math.max(0.001, ...(stacks.length ? stacks[stacks.length - 1] : [0]));

  const W = 800;
  const sx = (i) => (i / (N - 1)) * W;
  const sy = (v) => height - (v / yMax) * (height - 4) - 2;

  const tickFmt = (t) =>
    new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${height}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height, display: "block" }}
      >
        {(series || []).map((s, sIdx) => {
          let path = "";
          for (let i = 0; i < N; i++) {
            path += `${i === 0 ? "M" : "L"}${sx(i).toFixed(1)} ${sy(stacks[sIdx][i]).toFixed(1)} `;
          }
          for (let i = N - 1; i >= 0; i--) {
            const bot = sIdx === 0 ? 0 : stacks[sIdx - 1][i];
            path += `L${sx(i).toFixed(1)} ${sy(bot).toFixed(1)} `;
          }
          path += "Z";
          return (
            <path
              key={sIdx}
              d={path}
              fill={s.color}
              fillOpacity={0.55}
              stroke={s.color}
              strokeWidth={1}
            />
          );
        })}
      </svg>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "0.7rem",
          color: "#94a3b8",
          marginTop: 4,
        }}
      >
        <span>{tickFmt(tMin)}</span>
        <span>peak {yMax.toFixed(1)} {yLabel}</span>
        <span>{tickFmt(tMax)}</span>
      </div>
    </div>
  );
}

// ── WAN state ribbon (per-WAN healthy / degraded / down over time) ──────────
//
// Same time-domain math as ReachabilityStrip but with a third state
// (degraded) computed from loss_ratio against the dashboard threshold. Use
// when you want to show *why* a failover happened, not just whether the
// probe acked or didn't.
function WANStateRibbon({ series, windowSec, downIntervalsByWan }) {
  const wanName = useWanName();
  if (!series || series.length === 0) {
    return (
      <div style={{ color: "#94a3b8", fontSize: "0.85rem", padding: "0.5rem 0" }}>
        No probe data in this window.
      </div>
    );
  }
  let tMin = Infinity;
  let tMax = -Infinity;
  for (const s of series) {
    for (const p of s.points || []) {
      if (p.ts < tMin) tMin = p.ts;
      if (p.ts > tMax) tMax = p.ts;
    }
  }
  if (!isFinite(tMin) || tMax <= tMin) {
    tMax = Date.now();
    tMin = tMax - (windowSec || 3600) * 1000;
  }
  const span = tMax - tMin;
  const slotMs = span / RIBBON_BUCKETS;
  const tickFmt = (t) =>
    new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const labelW = 80;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {series.map((s) => {
        const slots = new Array(RIBBON_BUCKETS).fill(null);
        for (const p of s.points || []) {
          const idx = Math.min(
            RIBBON_BUCKETS - 1,
            Math.max(0, Math.floor((p.ts - tMin) / slotMs))
          );
          // Pick the worst (highest loss%) sample that fell in the slot, so
          // a single bad probe round colors the slot accordingly.
          if (slots[idx] == null || p.value > slots[idx]) slots[idx] = p.value;
        }
        const downIv = downIntervalsByWan?.get(s.name);
        return (
          <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: labelW,
                minWidth: labelW,
                fontSize: "0.78rem",
                fontWeight: 600,
                color: "#0f172a",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  background: s.color,
                  borderRadius: 2,
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{wanName(s.name)}</span>
            </div>
            <div
              style={{
                flex: 1,
                display: "grid",
                gridTemplateColumns: `repeat(${RIBBON_BUCKETS}, 1fr)`,
                gap: 1,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 4,
                padding: 2,
                height: 18,
              }}
            >
              {slots.map((v, i) => {
                let bg = "#e2e8f0";
                let lbl = "no data";
                if (v != null) {
                  if (v >= 100) {
                    bg = "#dc2626";
                    lbl = "down";
                  } else if (v >= RIBBON_DEGRADED_THRESHOLD_PCT) {
                    bg = "#f59e0b";
                    lbl = "degraded";
                  } else {
                    bg = "#16a34a";
                    lbl = "healthy";
                  }
                }
                // A recorded wan_down → wan_up interval is authoritative: the
                // link was gone (no samples emitted), so override "no data".
                const t0 = tMin + i * slotMs;
                if (slotIsDown(downIv, t0, t0 + slotMs)) {
                  bg = "#dc2626";
                  lbl = "down (link down)";
                }
                return (
                  <div
                    key={i}
                    title={`${tickFmt(tMin + i * slotMs)} — ${lbl}`}
                    style={{ background: bg, borderRadius: 1 }}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingLeft: labelW + 8,
          marginTop: 2,
          fontSize: "0.7rem",
          color: "#94a3b8",
        }}
      >
        <span>{tickFmt(tMin)}</span>
        <span>{tickFmt(tMax)}</span>
      </div>
      <div
        style={{
          display: "flex",
          gap: 14,
          fontSize: "0.72rem",
          color: "#475569",
          marginTop: 4,
          flexWrap: "wrap",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 10, height: 10, background: "#16a34a", borderRadius: 2 }} />
          healthy (loss &lt; {RIBBON_DEGRADED_THRESHOLD_PCT}%)
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 10, height: 10, background: "#f59e0b", borderRadius: 2 }} />
          degraded
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 10, height: 10, background: "#dc2626", borderRadius: 2 }} />
          down (100% loss)
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 10, height: 10, background: "#e2e8f0", borderRadius: 2 }} />
          no data
        </span>
      </div>
    </div>
  );
}

// computeAllDown takes the same per-WAN loss_ratio series the ribbon
// consumes and returns:
//   { tMin, tMax, slotMs, slots: [bool|null], allDownSec, availability }
// where slots[i] = true iff every WAN with data in slot i was at 100% loss
// (true tunnel outage), false iff at least one had healthy/degraded data,
// null iff no WAN had data in the slot. availability is computed only over
// slots where we had data (knownCount). This is the *real* tunnel-level
// availability the scorecard's per-WAN-summed approach gets wrong.
function computeAllDown(series, windowSec) {
  const empty = { tMin: 0, tMax: 0, slotMs: 0, slots: [], allDownSec: 0, availability: null };
  if (!series || series.length === 0) return empty;
  let tMin = Infinity;
  let tMax = -Infinity;
  for (const s of series) {
    for (const p of s.points || []) {
      if (p.ts < tMin) tMin = p.ts;
      if (p.ts > tMax) tMax = p.ts;
    }
  }
  if (!isFinite(tMin) || tMax <= tMin) {
    tMax = Date.now();
    tMin = tMax - (windowSec || 3600) * 1000;
  }
  const span = tMax - tMin;
  const slotMs = span / RIBBON_BUCKETS;
  // Pre-bucket each series for efficiency.
  const perWanBuckets = series.map((s) => {
    const slots = new Array(RIBBON_BUCKETS).fill(null);
    for (const p of s.points || []) {
      const idx = Math.min(
        RIBBON_BUCKETS - 1,
        Math.max(0, Math.floor((p.ts - tMin) / slotMs))
      );
      if (slots[idx] == null || p.value > slots[idx]) slots[idx] = p.value;
    }
    return slots;
  });
  const slots = new Array(RIBBON_BUCKETS).fill(null);
  let downCount = 0;
  let knownCount = 0;
  for (let i = 0; i < RIBBON_BUCKETS; i++) {
    let anyData = false;
    let allDown = true;
    for (const buckets of perWanBuckets) {
      const v = buckets[i];
      if (v == null) continue;
      anyData = true;
      if (v < 100) {
        allDown = false;
        break;
      }
    }
    if (anyData) {
      knownCount++;
      slots[i] = allDown;
      if (allDown) downCount++;
    }
  }
  const allDownSec = downCount * (slotMs / 1000);
  const availability = knownCount > 0 ? 1 - downCount / knownCount : null;
  return { tMin, tMax, slotMs, slots, allDownSec, availability };
}

// OutageStrip renders the result of computeAllDown as a single horizontal
// red-on-gray strip. Clean visual answer to "when was the tunnel actually
// offline?", separate from per-WAN-level state.
function OutageStrip({ tMin, tMax, slots, slotMs, allDownSec, availability }) {
  if (!slots || slots.length === 0) {
    return (
      <div style={{ color: "#94a3b8", fontSize: "0.85rem", padding: "0.5rem 0" }}>
        No probe data in this window.
      </div>
    );
  }
  const tickFmt = (t) =>
    new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const labelW = 80;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            width: labelW,
            minWidth: labelW,
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "#0f172a",
          }}
        >
          tunnel
        </div>
        <div
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: `repeat(${slots.length}, 1fr)`,
            gap: 1,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 4,
            padding: 2,
            height: 22,
          }}
        >
          {slots.map((v, i) => {
            let bg = "#e2e8f0";
            let lbl = "no data";
            if (v === true) {
              bg = "#dc2626";
              lbl = "tunnel offline (all WANs down)";
            } else if (v === false) {
              bg = "#16a34a";
              lbl = "tunnel online";
            }
            return (
              <div
                key={i}
                title={`${tickFmt(tMin + i * slotMs)} — ${lbl}`}
                style={{ background: bg, borderRadius: 1 }}
              />
            );
          })}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingLeft: labelW + 8,
          fontSize: "0.7rem",
          color: "#94a3b8",
        }}
      >
        <span>{tickFmt(tMin)}</span>
        <span>{tickFmt(tMax)}</span>
      </div>
      <div
        style={{
          display: "flex",
          gap: 14,
          fontSize: "0.78rem",
          color: "#0f172a",
          marginTop: 6,
          padding: "6px 10px",
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 6,
          flexWrap: "wrap",
        }}
      >
        <span>
          <strong>True availability:</strong>{" "}
          {availability == null ? "—" : `${(availability * 100).toFixed(2)}%`}
        </span>
        <span style={{ color: "#475569" }}>
          all-WANs-down time:{" "}
          <strong>{allDownSec < 60 ? `${allDownSec.toFixed(0)}s` : `${(allDownSec / 60).toFixed(1)}m`}</strong>
        </span>
        <span style={{ color: "#94a3b8", fontStyle: "italic" }}>
          (computed here from probe loss, over the time the node reported; the scorecard's
          availability comes from its line up and down events and its own stops and starts)
        </span>
      </div>
    </div>
  );
}

// ── WAN occupancy timeline (per-WAN active / standby / down swimlane) ────────
//
// Answers "how much time did the tunnel spend on each WAN, and when did it
// fall back?" — a Gantt-style swimlane, one lane per WAN, bucketed over the
// window. Each lane slot is one of:
//   • active  (WAN colour) — that WAN was carrying traffic (per-WAN throughput
//                            above ACTIVE_MBPS_THRESHOLD) in the slot
//   • standby (light blue) — WAN was reachable but ~idle (no traffic)
//   • down    (red)        — WAN was at 100% probe loss in the slot
//   • no data (grey)       — no samples in the slot
// The right-hand column shows each WAN's active time and the share of the
// window it actively carried the tunnel. Reuses the same per-WAN throughput
// and loss series the charts already fetch — no new endpoint.
const ACTIVE_MBPS_THRESHOLD = 0.05;

function bucketMaxSlots(points, tMin, slotMs) {
  const slots = new Array(RIBBON_BUCKETS).fill(null);
  for (const p of points || []) {
    const idx = Math.min(
      RIBBON_BUCKETS - 1,
      Math.max(0, Math.floor((p.ts - tMin) / slotMs))
    );
    if (slots[idx] == null || p.value > slots[idx]) slots[idx] = p.value;
  }
  return slots;
}

function WANOccupancyTimeline({ throughputSeries, reachSeries, windowSec, downIntervalsByWan }) {
  const wanName = useWanName();
  const names = useMemo(() => {
    const set = new Set();
    for (const s of throughputSeries || []) set.add(s.name);
    for (const s of reachSeries || []) set.add(s.name);
    return [...set].sort();
  }, [throughputSeries, reachSeries]);

  const thruByName = useMemo(() => {
    const m = new Map();
    for (const s of throughputSeries || []) m.set(s.name, s);
    return m;
  }, [throughputSeries]);

  const reachByName = useMemo(() => {
    const m = new Map();
    for (const s of reachSeries || []) m.set(s.name, s);
    return m;
  }, [reachSeries]);

  const { tMin, tMax } = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    const scan = (arr) => {
      for (const s of arr || []) {
        for (const p of s.points || []) {
          if (p.ts < lo) lo = p.ts;
          if (p.ts > hi) hi = p.ts;
        }
      }
    };
    scan(throughputSeries);
    scan(reachSeries);
    if (!isFinite(lo) || hi <= lo) {
      hi = Date.now();
      lo = hi - (windowSec || 3600) * 1000;
    }
    return { tMin: lo, tMax: hi };
  }, [throughputSeries, reachSeries, windowSec]);

  if (names.length === 0) {
    return (
      <div style={{ color: "#94a3b8", fontSize: "0.85rem", padding: "0.5rem 0" }}>
        No per-WAN samples in this window.
      </div>
    );
  }

  const span = tMax - tMin;
  const slotMs = span / RIBBON_BUCKETS;
  const slotSec = slotMs / 1000;
  const tickFmt = (t) =>
    new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const labelW = 96;
  const statW = 120;

  const lanes = names.map((name) => {
    const color = colorForWan(name);
    const thru = bucketMaxSlots(thruByName.get(name)?.points, tMin, slotMs);
    const loss = bucketMaxSlots(reachByName.get(name)?.points, tMin, slotMs);
    const downIv = downIntervalsByWan?.get(name);
    const states = new Array(RIBBON_BUCKETS).fill("nodata");
    let activeSlots = 0;
    let knownSlots = 0;
    for (let i = 0; i < RIBBON_BUCKETS; i++) {
      const t = thru[i];
      const l = loss[i];
      const t0 = tMin + i * slotMs;
      // A recorded link-down interval is authoritative even when the link
      // emitted no samples (interface removed → gap, not 100%-loss points).
      if (slotIsDown(downIv, t0, t0 + slotMs)) {
        states[i] = "down";
        knownSlots++;
        continue;
      }
      if (t == null && l == null) continue;
      knownSlots++;
      if (l != null && l >= 100) {
        states[i] = "down";
      } else if (t != null && t >= ACTIVE_MBPS_THRESHOLD) {
        states[i] = "active";
        activeSlots++;
      } else {
        states[i] = "standby";
      }
    }
    return { name, color, states, activeSlots, knownSlots };
  });

  const bgFor = (state, laneColor) => {
    switch (state) {
      case "active":
        return laneColor;
      case "standby":
        return "#dbeafe";
      case "down":
        return "#dc2626";
      default:
        return "#eef2f7";
    }
  };
  const labelFor = (state) => {
    switch (state) {
      case "active":
        return "carrying traffic";
      case "standby":
        return "standby (reachable, idle)";
      case "down":
        return "down (100% loss)";
      default:
        return "no data";
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {lanes.map((lane) => {
        const activePct =
          lane.knownSlots > 0 ? (lane.activeSlots / lane.knownSlots) * 100 : 0;
        const activeSec = lane.activeSlots * slotSec;
        return (
          <div key={lane.name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: labelW,
                minWidth: labelW,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span style={{ width: 9, height: 9, background: lane.color, borderRadius: "50%" }} />
              <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#0f172a" }}>
                {wanName(lane.name)}
              </span>
            </div>
            <div
              style={{
                flex: 1,
                display: "grid",
                gridTemplateColumns: `repeat(${RIBBON_BUCKETS}, 1fr)`,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 4,
                padding: 2,
                height: 20,
                overflow: "hidden",
              }}
            >
              {lane.states.map((st, i) => (
                <div
                  key={i}
                  title={`${tickFmt(tMin + i * slotMs)} — ${wanName(lane.name)}: ${labelFor(st)}`}
                  style={{ background: bgFor(st, lane.color) }}
                />
              ))}
            </div>
            <div
              style={{
                width: statW,
                minWidth: statW,
                textAlign: "right",
                fontSize: "0.72rem",
                color: "#475569",
              }}
            >
              <strong style={{ color: "#0f172a" }}>{activePct.toFixed(0)}%</strong>
              {" · "}
              {fmtSec(activeSec)}
            </div>
          </div>
        );
      })}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingLeft: labelW + 8,
          paddingRight: statW + 8,
          fontSize: "0.7rem",
          color: "#94a3b8",
        }}
      >
        <span>{tickFmt(tMin)}</span>
        <span>{tickFmt(tMax)}</span>
      </div>
      <div
        style={{
          display: "flex",
          gap: 14,
          fontSize: "0.72rem",
          color: "#475569",
          flexWrap: "wrap",
          marginTop: 2,
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 10, height: 10, background: "#64748b", borderRadius: 2 }} />
          carrying traffic (per-WAN colour)
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 10, height: 10, background: "#dbeafe", borderRadius: 2 }} />
          standby (reachable, idle)
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 10, height: 10, background: "#dc2626", borderRadius: 2 }} />
          down (100% loss)
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 10, height: 10, background: "#eef2f7", borderRadius: 2 }} />
          no data
        </span>
        <span style={{ color: "#94a3b8", fontStyle: "italic" }}>
          right column = active time &amp; share of window each WAN carried the tunnel
        </span>
      </div>
    </div>
  );
}

// ── Cause counter for the "Why did failovers happen?" panel ────────────────
//
// Aggregates failover events by cause + reason tags. Returns an array of
// { cause, reason, count } sorted by count descending. Pure function; the
// dashboard memoises it to avoid recomputing on every render.
function summariseCauses(events) {
  const out = new Map();
  for (const e of events || []) {
    if (e.type !== "failover") continue;
    const cause = e.tags?.cause || "unknown";
    const reason = e.tags?.reason || "";
    const key = `${cause}|${reason}`;
    const cur = out.get(key) || { cause, reason, count: 0 };
    cur.count++;
    out.set(key, cur);
  }
  return Array.from(out.values()).sort((a, b) => b.count - a.count);
}

const REACH_BUCKETS_LEGACY_GUARD = REACH_BUCKETS; // keep referenced — see ReachabilityStrip below
void REACH_BUCKETS_LEGACY_GUARD;

function ReachabilityStrip({ series, windowSec, downIntervalsByWan }) {
  const wanName = useWanName();
  if (!series || series.length === 0) {
    return (
      <div style={{ color: "#94a3b8", fontSize: "0.85rem", padding: "0.5rem 0" }}>
        No probe data in this window.
      </div>
    );
  }

  // Establish a common time domain. Use the union of all series so every
  // strip uses the same x-axis and slots line up vertically across WANs.
  let tMin = Infinity;
  let tMax = -Infinity;
  for (const s of series) {
    for (const p of s.points || []) {
      if (p.ts < tMin) tMin = p.ts;
      if (p.ts > tMax) tMax = p.ts;
    }
  }
  if (!isFinite(tMin) || !isFinite(tMax) || tMax <= tMin) {
    // Fall back to the requested window ending now, so we still render strips.
    tMax = Date.now();
    tMin = tMax - (windowSec || 3600) * 1000;
  }
  const span = tMax - tMin;
  const slotMs = span / REACH_BUCKETS;

  const labelWidth = 80;
  const tickFmt = (t) => {
    const d = new Date(t);
    if (span <= 3 * 3600 * 1000) {
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString([], { month: "short", day: "numeric" }) + " " +
      d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {series.map((s) => {
        // Bucketize: for each slot, mark reachable if ANY sample's loss < 1.0.
        const slots = new Array(REACH_BUCKETS).fill(null); // null = no data
        for (const p of s.points || []) {
          const idx = Math.min(
            REACH_BUCKETS - 1,
            Math.max(0, Math.floor((p.ts - tMin) / slotMs))
          );
          // p.value here is loss_ratio * 100 (percent) because the upstream
          // extractor multiplies by 100 to share the array with the line
          // chart. So "fully lost" == 100. Reachable means < 100.
          const reachable = p.value < 100;
          if (slots[idx] === null) slots[idx] = reachable;
          else slots[idx] = slots[idx] || reachable;
        }
        const downIv = downIntervalsByWan?.get(s.name);
        return (
          <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: labelWidth,
                minWidth: labelWidth,
                fontSize: "0.78rem",
                fontWeight: 600,
                color: "#0f172a",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  background: s.color,
                  borderRadius: 2,
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{wanName(s.name)}</span>
            </div>
            <div
              style={{
                flex: 1,
                display: "grid",
                gridTemplateColumns: `repeat(${REACH_BUCKETS}, 1fr)`,
                gap: 1,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 4,
                padding: 2,
                height: 18,
              }}
            >
              {slots.map((v, i) => (
                <div
                  key={i}
                  title={`${tickFmt(tMin + i * slotMs)} — ${
                    slotIsDown(downIv, tMin + i * slotMs, tMin + (i + 1) * slotMs)
                      ? "unreachable (link down)"
                      : v === null
                      ? "no data"
                      : v
                      ? "reachable"
                      : "unreachable"
                  }`}
                  style={{
                    background: slotIsDown(
                      downIv,
                      tMin + i * slotMs,
                      tMin + (i + 1) * slotMs
                    )
                      ? "#dc2626"
                      : v === null
                      ? "#e2e8f0"
                      : v
                      ? "#16a34a"
                      : "#dc2626",
                    borderRadius: 1,
                  }}
                />
              ))}
            </div>
          </div>
        );
      })}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingLeft: labelWidth + 8,
          marginTop: 2,
          fontSize: "0.7rem",
          color: "#94a3b8",
        }}
      >
        <span>{tickFmt(tMin)}</span>
        <span>{tickFmt(tMin + (tMax - tMin) / 2)}</span>
        <span>{tickFmt(tMax)}</span>
      </div>
      <div
        style={{
          display: "flex",
          gap: 14,
          fontSize: "0.72rem",
          color: "#475569",
          marginTop: 4,
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 10, height: 10, background: "#16a34a", borderRadius: 2 }} />
          reachable (probes acking)
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 10, height: 10, background: "#dc2626", borderRadius: 2 }} />
          unreachable (100% loss)
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 10, height: 10, background: "#e2e8f0", borderRadius: 2 }} />
          no data
        </span>
      </div>
    </div>
  );
}

// ── Main panel ──────────────────────────────────────────────────────────────

// What a phone's scorecard means differs from an edge's. Its availability is
// the time its tunnel was up, which a person decides, not a failure rate; a
// link "recovering" is someone coming home to their Wi-Fi; and it records no
// sessions, steering or failovers. So a phone gets what it used and when it
// was connected, in its own words, and the edge's claims stay with edges.
export function phoneHeadline(sc) {
  if (!sc) return "";
  const used = (sc.traffic?.total_tx_bytes || 0) + (sc.traffic?.total_rx_bytes || 0);
  return `${fmtBytes(used)} through the tunnel, connected ${fmtPct(sc.uptime?.availability, 0)} of the time`;
}

export default function PilotInsightsPanel({ selectedNode, serverApiUrl, phone = false }) {
  const wanName = useWanName();
  const [windowKey, setWindowKey] = useState("24h");
  const [scorecard, setScorecard] = useState(null);
  // Per-WAN sparkline series for the three probe metrics. Each is an array of
  // { name, color, points: [{ts, value}] }. They share a color per WAN so the
  // three charts read as a coherent dashboard rather than three separate
  // legends. Older telemetry payloads that omit jitter or loss simply produce
  // empty arrays — the chart renders an "no data" placeholder gracefully.
  const [wanLatencySeries, setWanLatencySeries] = useState([]);
  const [wanJitterSeries, setWanJitterSeries] = useState([]);
  const [wanLossSeries, setWanLossSeries] = useState([]);
  // Per-WAN reachability series — same source data as wanLossSeries but
  // rendered as a heat-strip (reachable / unreachable / no-data per time
  // bucket) so you can see at a glance which interface the control-plane
  // probes were getting through over time. Values are loss_ratio (0..1):
  // reachable iff < 1.0 in that bucket.
  const [wanReachSeries, setWanReachSeries] = useState([]);
  // Per-WAN tunnel-side throughput (tx+rx) in Mbps, derived from the same
  // wan_link timeseries that drives latency/jitter/loss above. Plotted as a
  // stacked area chart so a failover reads visually: one band collapses,
  // another rises, total height = aggregate bonded throughput at that
  // instant. Each entry is { name, color, points: [{ts, value}] }.
  const [wanThroughputSeries, setWanThroughputSeries] = useState([]);
  const [tunnelSeries, setTunnelSeries] = useState([]);
  const [events, setEvents] = useState([]);
  const [apps, setApps] = useState([]);
  // Per-app throughput over time for the top N apps. Top N is computed off
  // the apps state (which sorts by total bytes); each entry is again
  // { name, color, points: [{ts, value}] } with value = bps for that app
  // averaged over the slot. Forward-fills naturally because the cumulative
  // counter shipped by the edge only goes up.
  const [appOverTimeSeries, setAppOverTimeSeries] = useState([]);
  const [devices, setDevices] = useState([]);
  // Appliance draw, shipped by the edge's power sampler through telemetry.
  // Null on an edge that has no sensor or no build with the feature: the tile
  // is then simply absent, because a power KPI reading zero on a box that
  // cannot measure would be read as "it draws nothing".
  const [powerSummary, setPowerSummary] = useState(null);
  // Per-edge system metrics (cpu_pct, mem_used_pct, temp_c, ...). Each is
  // a single-series array keyed by metric name so the existing Sparklines
  // component can render it.
  const [systemSeries, setSystemSeries] = useState({
    cpu: [],
    memPct: [],
    temp: [],
    load: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdate, setLastUpdate] = useState(null);
  // Newest event seen by the fast poll; null until the first pass seeds it.
  const lastEventKey = useRef(null);
  // A full refresh can outlive the poll interval, and the event watch can
  // trigger one mid-flight. Without this the two stack and the slower
  // response wins, painting older data over newer.
  const inFlight = useRef(false);

  const win = useMemo(() => WINDOWS.find((w) => w.key === windowKey) || WINDOWS[2], [windowKey]);

  // Per-WAN link-down intervals derived from wan_down/wan_up events. Used to
  // paint heat-strips red during real outages even when the link emitted no
  // samples (interface removed → telemetry gap rather than 100%-loss points).
  const downIntervalsByWan = useMemo(() => buildWanDownIntervals(events), [events]);

  const fetchAll = useCallback(async () => {
    if (!selectedNode || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError("");
    try {
      const node = encodeURIComponent(selectedNode);
      // Seconds, not the button label. Go's time.ParseDuration has no day
      // unit, so a "7d" window silently parsed as the server's 24h default
      // and the 7-day scorecard was a 24-hour scorecard. Deriving both params
      // from win.seconds keeps them honest whatever the label says.
      const since = `${win.seconds}s`;
      const windowParam = since;

      // Settled, not all: one slow or failed sub-query used to reject the
      // whole batch, so a single hiccup left every chart on the page holding
      // its previous values with no way to tell. Each response is now applied
      // on its own, and only a total wipeout raises the banner.
      const results = await Promise.allSettled([
        tfetch(serverApiUrl(`/telemetry/scorecard?node=${node}&window=${windowParam}`)),
        tfetch(serverApiUrl(`/telemetry/timeseries?node=${node}&metric=wan_link&since=${since}&limit=${QUERY_LIMIT}`)),
        tfetch(serverApiUrl(`/telemetry/timeseries?node=${node}&metric=tunnel&since=${since}&limit=${QUERY_LIMIT}`)),
        // Top Apps: server-side `/telemetry/apps` aggregator drops the
        // edge-shipped per-app byte counter (which lives under the
        // misnamed field "packets" — the variable in scrapeDevices is
        // called `pkts` even though the AppBreakdown value is bytes).
        // Pulling raw timeseries samples here lets us aggregate client-
        // side without depending on a coordinated server change. Each
        // sample carries cumulative bytes for one (app, ip) pair, so we
        // take the LATEST sample per pair and sum across IPs.
        tfetch(serverApiUrl(`/telemetry/timeseries?node=${node}&metric=app&since=${since}&limit=10000`)),
        tfetch(serverApiUrl(`/telemetry/devices?node=${node}&window=${windowParam}`)),
        // Power. Its own query rather than a field on `system`, because an edge
        // that cannot measure ships no sample at all and the absence is the
        // signal — a zero in a shared metric could not carry that.
        tfetch(serverApiUrl(`/telemetry/timeseries?node=${node}&metric=power&since=${since}&limit=2000`)),
        // System metrics (cpu / mem / temp / load) per the edge's sysprobe
        // sampler. Surfaced in Pilot Insights so an operator can correlate
        // dashboard anomalies with edge-host pressure (thermal throttling,
        // memory exhaustion, etc.) without flipping over to Diagnostics.
        tfetch(serverApiUrl(`/telemetry/timeseries?node=${node}&metric=system&since=${since}&limit=${QUERY_LIMIT}`)),
      ]);
      const [scRes, wansRes, tunRes, appsRes, devRes, pwrRes, sysRes] = results.map((r) =>
        r.status === "fulfilled" ? r.value : null
      );
      if (results.every((r) => r.status === "rejected")) {
        throw results[0].reason || new Error("telemetry unreachable");
      }

      // A refused response clears the scorecard; an unreachable server keeps
      // the last one on screen rather than blanking the headline mid-outage.
      if (scRes?.ok) setScorecard(await scRes.json());
      else if (scRes) setScorecard(null);

      if (wansRes?.ok) {
        const json = await wansRes.json();
        // Single pass over wan_link samples extracts latency, jitter, and loss
        // into three parallel series-by-WAN buckets. Missing fields are simply
        // skipped so older edges that don't yet emit jitter/loss won't poison
        // the chart with NaN/0 points — they just produce shorter series.
        const byWan = new Map(); // wan -> { lat: [], jit: [], los: [], thru: [] }
        for (const s of json.samples || []) {
          const wan = s.tags?.wan;
          if (!wan) continue;
          if (!byWan.has(wan)) byWan.set(wan, { lat: [], jit: [], los: [], thru: [] });
          const ts = new Date(s.ts).getTime();
          if (!isFinite(ts)) continue;
          const bucket = byWan.get(wan);
          const lat = s.fields?.latency_ms;
          const jit = s.fields?.jitter_ms;
          const los = s.fields?.loss_ratio;
          if (typeof lat === "number" && isFinite(lat)) bucket.lat.push({ ts, value: lat });
          if (typeof jit === "number" && isFinite(jit)) bucket.jit.push({ ts, value: jit });
          if (typeof los === "number" && isFinite(los)) bucket.los.push({ ts, value: los * 100 });
          // Per-WAN tunnel throughput in Mbps, summed tx+rx. Stable across
          // edges that ship one or the other field as 0 when idle.
          const txbps = s.fields?.tx_bytes_per_sec;
          const rxbps = s.fields?.rx_bytes_per_sec;
          if (typeof txbps === "number" || typeof rxbps === "number") {
            const mbps = (((txbps || 0) + (rxbps || 0)) * 8) / 1e6;
            if (isFinite(mbps)) bucket.thru.push({ ts, value: mbps });
          }
        }
        // Build all three series in a stable order: alphabetical by WAN name.
        // The chart legends and the WAN ranking table consume these arrays
        // directly, so a stable iteration order keeps the legend chip order
        // from flipping between polls. Color comes from colorForWan(name),
        // which is independent of order — the sort is purely for the legend.
        const sortedNames = [...byWan.keys()].sort();
        const latArr = [];
        const jitArr = [];
        const losArr = [];
        const reachArr = [];
        const thruArr = [];
        for (const name of sortedNames) {
          const b = byWan.get(name);
          const color = colorForWan(name);
          b.lat.sort((a, c) => a.ts - c.ts);
          b.jit.sort((a, c) => a.ts - c.ts);
          b.los.sort((a, c) => a.ts - c.ts);
          b.thru.sort((a, c) => a.ts - c.ts);
          // Downsample for chart rendering. Reachability strip uses the raw
          // series so we don't lose 100%-loss buckets to averaging.
          if (b.lat.length) latArr.push({ name, color, points: downsamplePoints(b.lat) });
          if (b.jit.length) jitArr.push({ name, color, points: downsamplePoints(b.jit) });
          if (b.los.length) losArr.push({ name, color, points: downsamplePoints(b.los) });
          if (b.los.length) reachArr.push({ name, color, points: b.los });
          if (b.thru.length) thruArr.push({ name, color, points: downsamplePoints(b.thru) });
        }
        setWanLatencySeries(latArr);
        setWanJitterSeries(jitArr);
        setWanLossSeries(losArr);
        setWanReachSeries(reachArr);
        setWanThroughputSeries(thruArr);
      }

      if (tunRes?.ok) {
        const json = await tunRes.json();
        const samples = (json.samples || []).map((s) => ({
          ts: new Date(s.ts).getTime(),
          tx: s.fields?.tx_bytes || 0,
          rx: s.fields?.rx_bytes || 0,
        }));
        samples.sort((a, b) => a.ts - b.ts);
        // Convert cumulative bytes to bps using consecutive diffs.
        const txPts = [];
        const rxPts = [];
        for (let k = 1; k < samples.length; k++) {
          const dt = (samples[k].ts - samples[k - 1].ts) / 1000;
          if (dt <= 0) continue;
          const tx = ((samples[k].tx - samples[k - 1].tx) * 8) / dt / 1e6; // Mbps
          const rx = ((samples[k].rx - samples[k - 1].rx) * 8) / dt / 1e6;
          if (tx >= 0) txPts.push({ ts: samples[k].ts, value: tx });
          if (rx >= 0) rxPts.push({ ts: samples[k].ts, value: rx });
        }
        setTunnelSeries([
          { name: "tx (Mbps)", color: "#10b981", points: downsamplePoints(txPts) },
          { name: "rx (Mbps)", color: "#6366f1", points: downsamplePoints(rxPts) },
        ]);
      }

      if (appsRes?.ok) {
        const json = await appsRes.json();
        // Aggregate per-app bytes carried *within the window* from the raw
        // timeseries. The edge ships per-(app, ip) byte counters as cumulative
        // totals (under the misnamed field "packets" — see the fetch site), so
        // taking the latest value would report lifetime-since-first-seen, which
        // over-counts badly against the windowed tunnel-traffic KPI. Instead we
        // take the first→last delta per (app, ip) pair and sum across IPs.
        const firstLastPerPair = new Map(); // "app|ip" -> {app, firstTs, firstBytes, lastTs, lastBytes}
        // Keep the full raw byte-over-time series per (app, ip) so we can
        // build the per-app throughput chart by differentiating the
        // cumulative counter into per-slot bps.
        const samplesByAppIp = new Map(); // "app|ip" -> [{ts, bytes}, ...]
        for (const s of json.samples || []) {
          const app = s.tags?.app;
          const ip = s.tags?.ip || "";
          if (!app) continue;
          const ts = new Date(s.ts).getTime();
          if (!isFinite(ts)) continue;
          const bytes =
            (typeof s.fields?.bytes === "number" && s.fields.bytes > 0
              ? s.fields.bytes
              : s.fields?.packets) || 0;
          const key = `${app}|${ip}`;
          const fl = firstLastPerPair.get(key);
          if (!fl) {
            firstLastPerPair.set(key, { app, firstTs: ts, firstBytes: bytes, lastTs: ts, lastBytes: bytes });
          } else {
            if (ts < fl.firstTs) { fl.firstTs = ts; fl.firstBytes = bytes; }
            if (ts > fl.lastTs) { fl.lastTs = ts; fl.lastBytes = bytes; }
          }
          if (!samplesByAppIp.has(key)) samplesByAppIp.set(key, []);
          samplesByAppIp.get(key).push({ ts, bytes });
        }
        const perApp = new Map(); // app -> bytes (windowed delta)
        for (const v of firstLastPerPair.values()) {
          // Counter reset within the window (edge reboot) → fall back to last.
          let delta = v.lastBytes - v.firstBytes;
          if (delta < 0) delta = v.lastBytes;
          perApp.set(v.app, (perApp.get(v.app) || 0) + delta);
        }
        const arr = Array.from(perApp.entries())
          .map(([app, bytes]) => ({ app, label: app, bytes }))
          .sort((a, b) => b.bytes - a.bytes);
        const total = arr.reduce((s, a) => s + a.bytes, 0) || 1;
        arr.forEach((a) => (a.share = a.bytes / total));
        setApps(arr);

        // Build per-app throughput-over-time series for the top N apps.
        // Method: differentiate each (app, ip) cumulative-byte stream into
        // per-interval bps, then sum those bps streams across IPs to get
        // a per-app bps stream. Plotting them as a stacked area shows the
        // failover-then-recovery footprint per workload.
        const TOP_APP_COUNT = 5;
        const topApps = arr.slice(0, TOP_APP_COUNT).map((a) => a.app);
        const perAppPoints = new Map(); // app -> [{ts, value: bps}]
        for (const [key, pts] of samplesByAppIp.entries()) {
          const app = key.split("|", 1)[0];
          if (!topApps.includes(app)) continue;
          pts.sort((a, b) => a.ts - b.ts);
          for (let i = 1; i < pts.length; i++) {
            const dt = (pts[i].ts - pts[i - 1].ts) / 1000;
            if (dt <= 0) continue;
            const dB = pts[i].bytes - pts[i - 1].bytes;
            // Counter resets / boot wraps → skip negative.
            if (dB < 0) continue;
            const bps = (dB * 8) / dt;
            const ts = pts[i].ts;
            if (!perAppPoints.has(app)) perAppPoints.set(app, new Map());
            const ptMap = perAppPoints.get(app);
            // Sum across IPs at the same timestamp slot. Use 10 s slot.
            const slot = Math.floor(ts / 10000) * 10000;
            ptMap.set(slot, (ptMap.get(slot) || 0) + bps);
          }
        }
        const appColors = [
          "#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
        ];
        const appOverTime = topApps
          .map((app, idx) => {
            const ptMap = perAppPoints.get(app) || new Map();
            const points = Array.from(ptMap.entries())
              .map(([ts, bps]) => ({ ts, value: bps / 1e6 })) // Mbps
              .sort((a, b) => a.ts - b.ts);
            return {
              name: app.replace(/^APP_/, ""),
              color: appColors[idx % appColors.length],
              points: downsamplePoints(points),
            };
          })
          .filter((s) => s.points.length > 0);
        setAppOverTimeSeries(appOverTime);
      }

      if (devRes?.ok) {
        const json = await devRes.json();
        const arr = (json.devices || [])
          .map((d) => ({
            ...d,
            label: d.hostname || d.ip,
            sub: d.hostname ? d.ip : null,
            bytes: (d.tx_bytes || 0) + (d.rx_bytes || 0),
          }))
          .sort((a, b) => b.bytes - a.bytes);
        const total = arr.reduce((s, d) => s + d.bytes, 0) || 1;
        arr.forEach((d) => (d.share = d.bytes / total));
        setDevices(arr);
      }

      if (pwrRes?.ok) {
        const json = await pwrRes.json();
        const samples = json.samples || [];
        const latest = samples[samples.length - 1];
        if (latest?.fields?.watts > 0) {
          // Energy over the window comes from the per-sample 24h figure at the
          // end of it rather than by integrating: the edge already did that
          // arithmetic against its own clock, and re-deriving it here from a
          // sampled series would disagree with the box's own panel.
          setPowerSummary({
            watts: latest.fields.watts,
            kwh24: latest.fields.kwh_24h,
            provenance: latest.tags?.provenance || "",
            ts: latest.ts,
          });
        } else {
          setPowerSummary(null);
        }
      }

      // System metrics. The edge's sysprobe samples cpu/mem/temp/load every
      // 10 s; we slice each field into its own per-time series so the
      // dashboard can render compact sparklines without re-walking the
      // sample list once per metric.
      if (sysRes?.ok) {
        const json = await sysRes.json();
        const cpu = [];
        const mem = [];
        const temp = [];
        const load = [];
        for (const s of json.samples || []) {
          const ts = new Date(s.ts).getTime();
          if (!isFinite(ts)) continue;
          const f = s.fields || {};
          if (typeof f.cpu_pct === "number" && isFinite(f.cpu_pct)) {
            cpu.push({ ts, value: f.cpu_pct });
          }
          // Memory % used = mem_used_mb / mem_total_mb. Both fields
          // ship together; skip the slot if either is missing.
          if (typeof f.mem_used_mb === "number" && typeof f.mem_total_mb === "number" && f.mem_total_mb > 0) {
            mem.push({ ts, value: (f.mem_used_mb / f.mem_total_mb) * 100 });
          }
          if (typeof f.temp_c === "number" && f.temp_c > 0 && isFinite(f.temp_c)) {
            temp.push({ ts, value: f.temp_c });
          }
          if (typeof f.load_avg_1 === "number" && isFinite(f.load_avg_1)) {
            load.push({ ts, value: f.load_avg_1 });
          }
        }
        cpu.sort((a, b) => a.ts - b.ts);
        mem.sort((a, b) => a.ts - b.ts);
        temp.sort((a, b) => a.ts - b.ts);
        load.sort((a, b) => a.ts - b.ts);
        setSystemSeries({
          cpu: downsamplePoints(cpu),
          mem: downsamplePoints(mem),
          temp: downsamplePoints(temp),
          load: downsamplePoints(load),
        });
      }

      setLastUpdate(new Date());
    } catch (e) {
      setError(e?.message || "Failed to load telemetry");
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [selectedNode, serverApiUrl, win]);

  // The event feed on its own, faster loop. Besides keeping the feed live it
  // is the trigger for the rest of the page: when a disruptive event lands
  // (hard failover, WAN down/up, anchor switch) the heavy queries re-run at
  // once, so the charts and the scorecard show the failover instead of the
  // state from up to 15 s before it.
  const fetchEvents = useCallback(async () => {
    if (!selectedNode) return;
    const node = encodeURIComponent(selectedNode);
    const res = await fetchWithTimeout(
      serverApiUrl(`/telemetry/events?node=${node}&since=${win.seconds}s&limit=200`)
    ).catch(() => null);
    if (!res?.ok) return;
    const json = await res.json();
    const list = (json.events || []).slice().reverse(); // newest first
    setEvents(list);

    const key = eventKey(list[0]);
    const prev = lastEventKey.current;
    lastEventKey.current = key;
    // First pass after a node/window change only seeds the marker: fetchAll
    // is already running for that change.
    if (prev === null || key === prev || !key) return;
    if (DISRUPTIVE_EVENTS.has(list[0]?.type)) fetchAll();
  }, [selectedNode, serverApiUrl, win, fetchAll]);

  useVisibleInterval(fetchAll, FULL_POLL_MS, !!selectedNode);
  useVisibleInterval(fetchEvents, EVENT_POLL_MS, !!selectedNode);

  useEffect(() => {
    lastEventKey.current = null;
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode, windowKey]);

  if (!selectedNode) {
    return (
      <Card title="Pilot Insights">
        <div style={{ color: "#64748b" }}>Select a node from the sidebar to view pilot telemetry.</div>
      </Card>
    );
  }

  const sc = scorecard;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      {/* ── Headline + window selector ── */}
      <div
        style={{
          background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 60%, #db2777 100%)",
          color: "white",
          borderRadius: 16,
          padding: "1.5rem 1.75rem",
          display: "flex",
          flexWrap: "wrap",
          gap: "1.25rem",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 12px 30px rgba(79,70,229,0.25)",
        }}
      >
        <div style={{ minWidth: 0, flex: "1 1 360px" }}>
          <div style={{ fontSize: "0.78rem", letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.9 }}>
            Pilot scorecard · {selectedNode}
          </div>
          <div style={{ fontSize: "1.7rem", fontWeight: 800, lineHeight: 1.25, marginTop: "0.35rem" }}>
            {(phone ? phoneHeadline(sc) : sc?.headline_number) || "Awaiting telemetry…"}
          </div>
          <div style={{ fontSize: "0.85rem", opacity: 0.88, marginTop: "0.45rem" }}>
            {sc
              ? `Window: ${win.label.toLowerCase()} · ${new Date(sc.window_start).toLocaleString()} → ${new Date(
                  sc.window_end
                ).toLocaleString()}`
              : "Hold tight — first batch lands within ~15 s of edge boot."}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", alignItems: "flex-end" }}>
          <div style={{ display: "flex", gap: 6, background: "rgba(15,23,42,0.18)", padding: 4, borderRadius: 999 }}>
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                onClick={() => setWindowKey(w.key)}
                style={{
                  border: "none",
                  background: w.key === windowKey ? "white" : "transparent",
                  color: w.key === windowKey ? "#4f46e5" : "white",
                  padding: "0.35rem 0.85rem",
                  borderRadius: 999,
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                {w.label.replace("Last ", "")}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <div style={{ fontSize: "0.72rem", opacity: 0.85 }}>
              {loading ? "Refreshing…" : lastUpdate ? `Updated ${lastUpdate.toLocaleTimeString()}` : ""}
            </div>
            <button
              onClick={fetchAll}
              disabled={loading}
              title="Re-run every telemetry query now"
              style={{
                border: "1px solid rgba(255,255,255,0.45)",
                background: "rgba(15,23,42,0.18)",
                color: "white",
                padding: "0.3rem 0.7rem",
                borderRadius: 999,
                fontWeight: 600,
                fontSize: "0.72rem",
                cursor: loading ? "default" : "pointer",
                opacity: loading ? 0.6 : 1,
              }}
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ background: "#fee2e2", color: "#991b1b", padding: "0.75rem 1rem", borderRadius: 8 }}>
          {error}
        </div>
      )}

      {/* ── KPI grid ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: "1rem",
        }}
      >
        {phone ? (
          <>
            <KPI
              label="Time connected"
              value={fmtPct(sc?.uptime?.availability)}
              sub={`${fmtSec(sc?.uptime?.online_seconds)} connected · longest time off ${fmtSec(sc?.uptime?.longest_outage_seconds)}`}
              accent="#10b981"
            />
            <KPI
              label="Data used"
              value={fmtBytes((sc?.traffic?.total_tx_bytes || 0) + (sc?.traffic?.total_rx_bytes || 0))}
              sub={
                (sc?.wan_ranking || [])
                  .map((w) => `${wanName(w.wan)} ${fmtBytes((w.bytes_tx || 0) + (w.bytes_rx || 0))}`)
                  .join(" · ") || "no link has reported yet"
              }
              accent="#6366f1"
            />
          </>
        ) : (
          <KPI
            label="Availability"
            value={fmtPct(sc?.uptime?.availability)}
            sub={`${fmtSec(sc?.uptime?.online_seconds)} online · longest outage ${fmtSec(sc?.uptime?.longest_outage_seconds)}`}
            accent="#10b981"
          />
        )}
        {!phone && <KPI
          label="Failovers"
          value={fmtNum(sc?.failover?.failover_count ?? 0)}
          sub={`${fmtNum(sc?.failover?.soft_failover_count ?? 0)} soft · ${fmtNum(
            sc?.failover?.hard_failover_count ?? 0
          )} hard · carrier link recovery ${fmtSec(sc?.failover?.link_recovery_seconds ?? sc?.failover?.mttr_seconds)}`}
          accent="#f59e0b"
        />}
        {/* Not "sessions preserved". This is the SUM of the conntrack flow count
            taken at each reroute instant, so N reroutes over the same 8 flows
            reads as 8N. Nothing in the pipeline checks afterwards whether any
            session reset, so calling it "preserved" asserted a negative from no
            evidence — and it is the first number a technical buyer picks apart.
            Sessions do survive, because the public IP belongs to the anchor and
            the inner 5-tuple never changes, but that is an argument from the
            architecture, not this figure. */}
        {!phone && <KPI
          label="Sessions in flight at failover"
          value={fmtNum(sc?.failover?.sessions_preserved_estimate ?? 0)}
          sub={
            sc?.failover?.sessions_measured
              ? `summed across ${fmtNum(sc?.failover?.failover_count ?? 0)} reroutes (conntrack at each instant) — not distinct sessions`
              : "estimated (×8 heuristic — no measured failover data in window)"
          }
          accent="#22c55e"
        />}
        <KPI
          label="Bonding gain"
          value={sc?.bonding?.gain_multiplier ? fmtGain(sc.bonding.gain_multiplier) : "—"}
          sub={`${fmtMbps(sc?.bonding?.aggregate_mbps_p95)} aggregate vs ${fmtMbps(
            sc?.bonding?.best_single_wan_mbps_p95
          )} best link · ${fmtBytes(sc?.bonding?.bytes_routed_via_secondary)} via secondary WAN`}
          accent="#6366f1"
        />
        <KPI
          label="Multi-WAN concurrency"
          value={fmtPct(sc?.bonding?.concurrent_wan_ratio)}
          sub="of intervals ≥2 WANs each carried ≥5% of the traffic — a real split, not probe traffic on an idle standby"
          accent="#14b8a6"
        />
        {powerSummary && (
          <KPI
            label={powerSummary.provenance === "measured_cpu" ? "Processor draw" : "Power draw"}
            value={`${powerSummary.watts.toFixed(1)} W`}
            sub={
              {
                measured: "whole appliance, measured at the box",
                measured_cpu: "CPU package only — no whole-box sensor on this hardware",
                modeled_platform: "processor measured, rest of the box from a set baseline",
                modeled_nameplate: "estimated from a configured rating",
              }[powerSummary.provenance] || "see the Power & Cost panel"
            }
            accent="#0891b2"
          />
        )}
        {!phone && (
          <>
            <KPI
              label="Downtime avoided"
              value={fmtSec(sc?.uptime?.downtime_avoided_seconds)}
              sub="edge stayed online on another WAN while a link was down — offline on single-WAN"
              accent="#0d9488"
            />
            <KPI
              label="App-aware steering"
              value={fmtNum(sc?.steering?.distinct_classes_steered ?? 0)}
              sub={`distinct traffic classes steered · ${fmtNum(
                sc?.steering?.total_class_moves ?? 0
              )} per-class WAN moves`}
              accent="#a855f7"
            />
          </>
        )}
        <KPI
          label="QoS in SLA"
          value={fmtPct(sc?.qos?.percent_in_sla)}
          sub={`avg ${fmtMs(sc?.qos?.avg_latency_ms)} · ${fmtNum(sc?.qos?.switch_events ?? 0)} switches · ${fmtNum(
            sc?.qos?.breach_events ?? 0
          )} breaches`}
          accent="#0ea5e9"
        />
        <KPI
          label={`Traffic · ${win.label}`}
          value={fmtBytes((sc?.traffic?.total_tx_bytes || 0) + (sc?.traffic?.total_rx_bytes || 0))}
          sub={`tx ${fmtBytes(sc?.traffic?.total_tx_bytes)} · rx ${fmtBytes(sc?.traffic?.total_rx_bytes)} · selected window only`}
          accent="#ec4899"
        />
        {/* A phone's counters restart with each connection, so the peak of one
            says nothing about all time. */}
        {!phone && <KPI
          label="Traffic · all time"
          value={fmtBytes((sc?.traffic?.all_time_tx_bytes || 0) + (sc?.traffic?.all_time_rx_bytes || 0))}
          sub={`tx ${fmtBytes(sc?.traffic?.all_time_tx_bytes)} · rx ${fmtBytes(sc?.traffic?.all_time_rx_bytes)} · since edge deploy`}
          accent="#f472b6"
        />}
        <KPI
          label="Peak throughput"
          value={
            (sc?.traffic?.peak_tx_bps || 0) + (sc?.traffic?.peak_rx_bps || 0) > 0
              ? `${(
                  ((sc?.traffic?.peak_tx_bps || 0) + (sc?.traffic?.peak_rx_bps || 0)) *
                    8 /
                    1e6
                ).toFixed(1)} Mbps`
              : "—"
          }
          sub={`tx peak ${fmtMbps(((sc?.traffic?.peak_tx_bps || 0) * 8) / 1e6)} · rx peak ${fmtMbps(
            ((sc?.traffic?.peak_rx_bps || 0) * 8) / 1e6
          )}`}
          accent="#7c3aed"
        />
      </div>

      {/* ── Charts row ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
          gap: "1rem",
        }}
      >
        <Card title="Per-WAN latency" subtitle="Active health probes — RTT per WAN over the selected window">
          <Sparklines series={wanLatencySeries} yLabel="ms" />
          <LegendRow series={wanLatencySeries} />
        </Card>

        <Card title="Per-WAN jitter" subtitle="Active health probes — RTT variance per WAN">
          <Sparklines series={wanJitterSeries} yLabel="ms" />
          <LegendRow series={wanJitterSeries} />
        </Card>

        <Card title="Per-WAN packet loss" subtitle="Active health probes — fraction of probes lost per WAN">
          <Sparklines series={wanLossSeries} yLabel="%" />
          <LegendRow series={wanLossSeries} />
        </Card>

        <Card title="Tunnel throughput" subtitle="Aggregate tx / rx through the bonded tunnel">
          <Sparklines series={tunnelSeries} yLabel="Mbps" />
          <LegendRow series={tunnelSeries} />
        </Card>
      </div>

      {/* ── Probe-path reachability ── */}
      <Card
        title="Probe path reachability"
        subtitle="Per-WAN control-plane reachability over the selected window — green = probes acking, red = 100% loss, gray = no samples"
      >
        <ReachabilityStrip series={wanReachSeries} windowSec={win.seconds} downIntervalsByWan={downIntervalsByWan} />
      </Card>

      {/* ── Per-WAN traffic flow + state ribbon ── The honest "WAN
          switchover journey" view: stacked area shows actual bytes
          traversing each WAN over time (sourced from passive ifstats
          deltas, NOT synthetic probes), so a failover lands as a visible
          handoff between coloured bands. The ribbon below tags each WAN's
          state in the same time domain so you can read which transition
          drove which traffic shift. */}
      <Card
        title="Per-WAN traffic flow"
        subtitle="Live tx+rx Mbps per WAN — stacked total = bonded tunnel throughput. Below: each WAN's reachability state."
        accent="#6366f1"
      >
        <StackedAreaChart
          series={wanThroughputSeries}
          height={200}
          yLabel="Mbps"
          emptyMsg="No tunnel traffic in this window."
        />
        <div style={{ marginTop: 14 }}>
          <WANStateRibbon series={wanReachSeries} windowSec={win.seconds} downIntervalsByWan={downIntervalsByWan} />
        </div>
        <LegendRow series={wanThroughputSeries} />
      </Card>

      {/* ── True outage timeline + cause breakdown + system health ──
          A row of three diagnostic cards: when was the tunnel actually
          offline (vs scorecard's pessimistic per-WAN sum), why failovers
          fired, and how the edge host itself was doing. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
          gap: "1rem",
        }}
      >
        {(() => {
          const od = computeAllDown(wanReachSeries, win.seconds);
          return (
            <Card
              title="True outage timeline"
              subtitle="When was the bonded tunnel actually offline? Computed as the time intervals where every WAN was at 100% probe loss simultaneously."
              accent="#dc2626"
            >
              <OutageStrip
                tMin={od.tMin}
                tMax={od.tMax}
                slots={od.slots}
                slotMs={od.slotMs}
                allDownSec={od.allDownSec}
                availability={od.availability}
              />
            </Card>
          );
        })()}

        {!phone && (() => {
          const causes = summariseCauses(events);
          return (
            <Card
              title="Why failovers happened"
              subtitle="Each failover event tagged by cause and the reason the health detector reported."
              accent="#f59e0b"
            >
              {causes.length === 0 ? (
                <div style={{ color: "#94a3b8", fontSize: "0.85rem", padding: "0.5rem 0" }}>
                  No failover events in this window.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {causes.slice(0, 8).map((c, i) => {
                    const meta = causeMeta(c.cause);
                    return (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "6px 10px",
                          border: `1px solid ${meta.border}`,
                          background: meta.bg,
                          borderRadius: 6,
                          fontSize: "0.82rem",
                        }}
                      >
                        <span style={{ fontWeight: 700, color: meta.color, minWidth: 90 }}>
                          {meta.label}
                        </span>
                        <span style={{ color: "#475569", flex: 1 }}>
                          {c.reason ? prettyReason(c.reason) : "—"}
                        </span>
                        <span
                          style={{
                            fontWeight: 800,
                            color: meta.color,
                            background: "white",
                            border: `1px solid ${meta.border}`,
                            borderRadius: 999,
                            padding: "1px 9px",
                            fontSize: "0.78rem",
                          }}
                        >
                          ×{c.count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })()}

        {!phone && <Card
          title="Edge host health"
          subtitle="CPU, memory, temperature, load — useful for correlating dashboard anomalies with edge-host pressure."
          accent="#0ea5e9"
        >
          {(() => {
            const cpuLast = systemSeries.cpu?.length ? systemSeries.cpu[systemSeries.cpu.length - 1].value : null;
            const memLast = systemSeries.mem?.length ? systemSeries.mem[systemSeries.mem.length - 1].value : null;
            const tempLast = systemSeries.temp?.length ? systemSeries.temp[systemSeries.temp.length - 1].value : null;
            const loadLast = systemSeries.load?.length ? systemSeries.load[systemSeries.load.length - 1].value : null;
            const noData =
              !systemSeries.cpu?.length &&
              !systemSeries.mem?.length &&
              !systemSeries.temp?.length &&
              !systemSeries.load?.length;
            if (noData) {
              return (
                <div style={{ color: "#94a3b8", fontSize: "0.85rem", padding: "0.5rem 0" }}>
                  No edge host samples yet — takes ~10 s after edge boot.
                </div>
              );
            }
            const SparkRow = ({ label, points, color, fmt, last }) => (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "66px 1fr 70px",
                  alignItems: "center",
                  gap: 8,
                  paddingBottom: 4,
                }}
              >
                <span style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  {label}
                </span>
                <Sparklines
                  series={points && points.length ? [{ name: label, color, points }] : []}
                  height={36}
                  yLabel=""
                />
                <span
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: "#0f172a",
                    textAlign: "right",
                  }}
                >
                  {last == null ? "—" : fmt(last)}
                </span>
              </div>
            );
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <SparkRow
                  label="CPU"
                  points={systemSeries.cpu}
                  color="#6366f1"
                  last={cpuLast}
                  fmt={(v) => `${v.toFixed(1)}%`}
                />
                <SparkRow
                  label="Mem"
                  points={systemSeries.mem}
                  color="#10b981"
                  last={memLast}
                  fmt={(v) => `${v.toFixed(1)}%`}
                />
                <SparkRow
                  label="Temp"
                  points={systemSeries.temp}
                  color="#f97316"
                  last={tempLast}
                  fmt={(v) => `${v.toFixed(1)}°C`}
                />
                <SparkRow
                  label="Load"
                  points={systemSeries.load}
                  color="#8b5cf6"
                  last={loadLast}
                  fmt={(v) => v.toFixed(2)}
                />
              </div>
            );
          })()}
        </Card>}
      </div>

      {/* ── Top apps over time. Stacked area of the top 5 apps' bps,
          differentiated client-side from cumulative byte counters. Lets
          an operator see when the speedtest happened, when the iCloud
          sync ran, etc. */}
      {/* A phone ships no apps and no devices: they would describe one person. */}
      {!phone && <Card
        title="Top applications over time"
        subtitle="Top-5 apps by total bytes — each band is that app's instantaneous Mbps. Differentiated client-side from cumulative byte counters."
        accent="#6366f1"
      >
        <StackedAreaChart
          series={appOverTimeSeries}
          height={180}
          yLabel="Mbps"
          emptyMsg="No per-app traffic samples in this window."
        />
        <LegendRow series={appOverTimeSeries} />
      </Card>}

      {/* ── WAN ranking + Top apps/devices ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
          gap: "1rem",
        }}
      >
        <Card title="WAN ranking" subtitle="Avg score, latency, loss, and bytes per WAN">
          {sc?.wan_ranking && sc.wan_ranking.length > 0 ? (
            <table style={{ width: "100%", fontSize: "0.85rem", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>
                  <th style={{ padding: "0.4rem 0.25rem", fontWeight: 600 }}>WAN</th>
                  <th style={{ padding: "0.4rem 0.25rem", fontWeight: 600 }}>Score</th>
                  <th style={{ padding: "0.4rem 0.25rem", fontWeight: 600 }}>Latency</th>
                  <th style={{ padding: "0.4rem 0.25rem", fontWeight: 600 }}>Loss</th>
                  <th style={{ padding: "0.4rem 0.25rem", fontWeight: 600 }}>Bytes (tx/rx)</th>
                </tr>
              </thead>
              <tbody>
                {sc.wan_ranking.map((w) => (
                  <tr key={w.wan} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "0.5rem 0.25rem", fontWeight: 600, color: "#0f172a" }}>
                      <span
                        style={{
                          display: "inline-block",
                          width: 8,
                          height: 8,
                          background: colorForWan(w.wan),
                          borderRadius: "50%",
                          marginRight: 8,
                        }}
                      />
                      {wanName(w.wan)}
                    </td>
                    <td style={{ padding: "0.5rem 0.25rem", color: "#0f172a" }}>{w.avg_score?.toFixed(2) ?? "—"}</td>
                    <td style={{ padding: "0.5rem 0.25rem", color: "#475569" }}>{fmtMs(w.avg_latency_ms)}</td>
                    <td style={{ padding: "0.5rem 0.25rem", color: "#475569" }}>{fmtPct(w.avg_loss_ratio, 2)}</td>
                    <td style={{ padding: "0.5rem 0.25rem", color: "#475569" }}>
                      {fmtBytes(w.bytes_tx)} / {fmtBytes(w.bytes_rx)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ color: "#94a3b8", fontSize: "0.85rem" }}>No WAN samples in this window.</div>
          )}
        </Card>

        {!phone && (
          <>
            <Card title="Top applications" subtitle="By bytes carried (computed client-side)">
              <ShareList items={apps} valueKey="bytes" shareKey="share" labelKey="label" color="#6366f1" />
            </Card>

            <Card title="Top devices" subtitle="LAN endpoints by usage">
              <ShareList items={devices} valueKey="bytes" shareKey="share" labelKey="label" subKey="sub" color="#10b981" />
            </Card>
          </>
        )}
      </div>

      {/* ── WAN occupancy timeline (swimlane) ── */}
      <Card
        title="WAN occupancy timeline"
        subtitle="Which WAN carried the tunnel over time and when it fell back — one lane per WAN. The right column is the share of the window each WAN actively carried traffic."
        accent="#10b981"
      >
        <WANOccupancyTimeline
          throughputSeries={wanThroughputSeries}
          reachSeries={wanReachSeries}
          windowSec={win.seconds}
          downIntervalsByWan={downIntervalsByWan}
        />
      </Card>

      {/* ── Failover timeline ── */}
      {!phone && <Card
        title="Failover timeline"
        subtitle="Each failover with its cause, reason, and recovery time. Click a row to expand."
        accent="#f59e0b"
      >
        <FailoverTimeline events={events} />
      </Card>}

      {/* ── Recent events ── */}
      <Card title="Recent events" subtitle="Latest first">
        {events.length === 0 ? (
          <div style={{ color: "#94a3b8", fontSize: "0.85rem" }}>No events in this window.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxHeight: 360, overflowY: "auto" }}>
            {events.map((e, i) => {
              const c = severityColor(e.severity);
              return (
                <div
                  key={i}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto auto 1fr auto",
                    gap: "0.75rem",
                    alignItems: "center",
                    padding: "0.5rem 0.6rem",
                    border: `1px solid ${c.border}`,
                    background: c.bg,
                    borderRadius: 8,
                    fontSize: "0.85rem",
                  }}
                >
                  <span style={{ fontWeight: 700, color: c.color, fontSize: "1rem", width: 20, textAlign: "center" }}>
                    {eventGlyph(e.type)}
                  </span>
                  <span style={{ fontWeight: 600, color: c.color, minWidth: 100 }}>{e.type}</span>
                  <span style={{ color: "#475569" }}>
                    {e.message || (e.tags ? Object.entries(e.tags).map(([k, v]) => `${k}=${v}`).join(" · ") : "")}
                    {e.source && (
                      <span style={{ color: "#94a3b8", marginLeft: 8 }}>· {e.source}</span>
                    )}
                  </span>
                  <span style={{ color: "#64748b", fontSize: "0.75rem", whiteSpace: "nowrap" }}>{fmtTs(e.ts)}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
